import axios from 'axios';
import AuthService from './auth-header';
import { resolveInvoiceApiUrl } from '@/utils/invoice-api-url';

const API_URL = resolveInvoiceApiUrl()

class EnhancedInvoiceService {
    // Invoice CRUD operations
    async create(data) {
        try {
            const headers = (typeof FormData !== 'undefined' && data instanceof FormData)
                ? AuthService.authHeaderForm()
                : AuthService.authHeader()
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
                delete headers['Content-Type']
            }
            const res = await axios.post(API_URL + 'invoice', data, { headers });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async upload(data) {
        try {
            const res = await axios.post(API_URL + 'invoice/upload', data, {
                headers: AuthService.authHeaderForm()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Master table & metadata
    async getMasterTableData(locationId) {
        try {
            const params = locationId ? { locationId } : {};
            const res = await axios.get(API_URL + 'invoice/master-table', {
                params,
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async getRateCard(clientId, vertical, category) {
        try {
            const params = {};
            if (clientId) params.clientId = clientId;
            if (vertical) params.vertical = vertical;
            if (category) params.category = category;
            const res = await axios.get(API_URL + 'invoice/config/rate-card', {
                params,
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Candidate lookup
    async lookupCandidateByName(candidateName, companyName) {
        try {
            const params = {};
            if (candidateName) params.candidateName = candidateName;
            if (companyName) params.companyName = companyName;
            params.status = 'Joined';
            const res = await axios.get(API_URL + 'invoice/candidate/lookup', {
                params,
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async mapCandidateToInvoice(invoiceId, candidateId, employeeId) {
        try {
            const data = { invoiceId, candidateId, employeeId };
            const res = await axios.post(API_URL + 'invoice/candidate/map', data, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Notifications
    async scheduleNotifications(invoiceId, creditPeriod) {
        try {
            const data = { invoiceId, creditPeriod };
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/schedule-notifications', data, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async getNotificationSchedule(invoiceId) {
        try {
            const res = await axios.get(API_URL + 'invoice/' + invoiceId + '/notifications', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async triggerNotification(invoiceId, notificationType) {
        try {
            const data = { notificationType };
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/trigger-notification', data, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Revision
    async initiateRevision(invoiceId, revisionType) {
        try {
            const data = { revisionType };
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/revision/initiate', data, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async uploadRevisionDocument(invoiceId, documentType, file) {
        try {
            const formData = new FormData();
            formData.append('document', file);
            formData.append('documentType', documentType);
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/revision/document', formData, {
                headers: AuthService.authHeaderForm()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async getRevisionHistory(invoiceId) {
        try {
            const res = await axios.get(API_URL + 'invoice/' + invoiceId + '/revision/history', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // E-Invoice
    async generateEInvoiceJSON(invoiceId) {
        try {
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/einvoice/generate-json', {}, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async generateBulkEInvoiceJSON(month) {
        try {
            const params = { month };
            const res = await axios.post(API_URL + 'invoice/einvoice/generate-bulk-json', {}, {
                params,
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async updateEInvoiceFilingStatus(invoiceId, status, filingData = {}) {
        try {
            const data = { status, ...filingData };
            const res = await axios.patch(API_URL + 'invoice/' + invoiceId + '/einvoice/filing-status', data, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            const statusCode = err.response && err.response.status;
            const serverMsg = err.response && err.response.data && (err.response.data.message || err.response.data.error);
            const message = serverMsg
                || (statusCode === 404
                    ? 'E-invoice filing-status endpoint not found. Backend must implement PATCH /api/invoice/:id/einvoice/filing-status.'
                    : 'Failed to update e-invoice filing status.');
            const error = new Error(message);
            error.response = err.response;
            throw error;
        }
    }

    async getEInvoiceFilingStatus(month) {
        try {
            const params = { month };
            const res = await axios.get(API_URL + 'invoice/einvoice/filing-status', {
                params,
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Booking — path key is business invoice_id (e.g. "TEST 2" / "SH180"), not numeric PK
    async checkBookingStatus(invoiceId) {
        const path = API_URL + 'invoice/' + encodeURIComponent(invoiceId) + '/booking/status';
        try {
            const res = await axios.get(path, {
                headers: AuthService.authHeader()
            });
            const body = res.data;
            if (body && body.status !== 'OK') {
                const msg = String((body && body.message) || '').toLowerCase();
                if (res.status === 404 || msg.includes('not found')) {
                    return { status: 'OK', data: null };
                }
            }
            return body;
        } catch (err) {
            const statusCode = err.response && err.response.status;
            const serverMsg = err.response && err.response.data
                && (err.response.data.message || err.response.data.error);
            if (statusCode === 404 || /not found/i.test(String(serverMsg || ''))) {
                return { status: 'OK', data: null };
            }
            console.log(err.response);
            return (err.response && err.response.data) || err.response;
        }
    }

    async updateBookingStatus(invoiceId, bookingData) {
        try {
            const res = await axios.put(API_URL + 'invoice/' + encodeURIComponent(invoiceId) + '/booking-status', bookingData, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            const statusCode = err.response && err.response.status;
            const serverMsg = err.response && err.response.data && (err.response.data.message || err.response.data.error);
            const message = serverMsg
                || (statusCode === 404
                    ? 'Booking-status endpoint not found. Deploy backend PUT /api/invoice/:id/booking-status.'
                    : 'Failed to update booking status.');
            const error = new Error(message);
            error.response = err.response;
            throw error;
        }
    }

    async getInvoicesRequiringBookingCheck() {
        try {
            const res = await axios.get(API_URL + 'invoice/booking/pending-check', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async getInvoicesApproachingBookingCheck() {
        try {
            const res = await axios.get(API_URL + 'invoice/booking/approaching-check', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Document upload
    async uploadDocument(invoiceId, formData) {
        try {
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/documents', formData, {
                headers: AuthService.authHeaderForm()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async getDocuments(invoiceId) {
        try {
            const res = await axios.get(API_URL + 'invoice/' + invoiceId + '/documents', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    async deleteDocument(invoiceId, documentId) {
        try {
            const res = await axios.delete(API_URL + 'invoice/' + invoiceId + '/documents/' + documentId, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // ── Signed invoice workflow (persist on server — not browser storage) ──
    // See backend-handoff/INVOICE_TEMPLATES_API_DOCUMENTATION.md §17.
    // Preferred routes: /invoice/:id/signed-invoice and /invoice/:id/generated-pdf.
    // Fallback: generic /documents with documentType signed_invoice | generated_pdf.

    _isOkEnvelope(res) {
        if (!res) return false;
        const st = res.status;
        return st === 'OK' || st === 200 || (typeof st === 'string' && String(st).toLowerCase() === 'ok');
    }

    _normalizeSignedList(payload) {
        if (!payload) return [];
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.signed_invoices)) return payload.signed_invoices;
        if (Array.isArray(payload.documents)) return payload.documents;
        if (payload.data && Array.isArray(payload.data.signed_invoices)) return payload.data.signed_invoices;
        if (payload.data && Array.isArray(payload.data.documents)) return payload.data.documents;
        // Single current signed file object
        if (payload.id || payload.file_path || payload.url || payload.file_name) return [payload];
        if (payload.data && (payload.data.id || payload.data.file_path || payload.data.url)) return [payload.data];
        return [];
    }

    async uploadSignedInvoice(invoiceId, file, meta = {}) {
        if (!invoiceId) return { status: 'ERROR', message: 'Invoice id is required.' };
        if (!file) return { status: 'ERROR', message: 'Signed PDF file is required.' };
        const formData = new FormData();
        formData.append('file', file);
        formData.append('signed_invoice', file);
        formData.append('document', file);
        formData.append('documentType', 'signed_invoice');
        if (meta.notes) formData.append('notes', meta.notes);
        if (meta.description) formData.append('description', meta.description);

        try {
            const res = await axios.post(
                API_URL + 'invoice/' + encodeURIComponent(invoiceId) + '/signed-invoice',
                formData,
                { headers: AuthService.authHeaderForm() }
            );
            return res.data;
        } catch (err) {
            const code = err && err.response && err.response.status;
            // Dedicated route missing — fall back to generic documents API.
            if (code === 404 || code === 501 || code === 405) {
                try {
                    const fallback = await this.uploadDocument(invoiceId, formData);
                    if (this._isOkEnvelope(fallback)) {
                        return {
                            ...fallback,
                            _source: 'documents-fallback',
                            message: (fallback && fallback.message) || 'Signed invoice stored via documents API.',
                        };
                    }
                    return fallback || { status: 'ERROR', message: 'Failed to upload signed invoice.' };
                } catch (fallbackErr) {
                    console.log(fallbackErr.response);
                    return fallbackErr.response || { status: 'ERROR', message: 'Failed to upload signed invoice.' };
                }
            }
            console.log(err.response);
            return (err.response && err.response.data) || err.response || {
                status: 'ERROR',
                message: 'Failed to upload signed invoice. Backend signed-invoice API is required.',
            };
        }
    }

    async listSignedInvoices(invoiceId) {
        if (!invoiceId) return { status: 'ERROR', data: [], message: 'Invoice id is required.' };
        try {
            const res = await axios.get(
                API_URL + 'invoice/' + encodeURIComponent(invoiceId) + '/signed-invoice',
                { headers: AuthService.authHeader() }
            );
            const body = res.data;
            if (this._isOkEnvelope(body)) {
                return { status: 'OK', data: this._normalizeSignedList(body), _source: 'signed-invoice' };
            }
            return { status: 'ERROR', data: [], message: (body && body.message) || 'Failed to load signed invoices.' };
        } catch (err) {
            const code = err && err.response && err.response.status;
            if (code === 404 || code === 501 || code === 405) {
                try {
                    const docs = await this.getDocuments(invoiceId);
                    if (this._isOkEnvelope(docs)) {
                        const all = this._normalizeSignedList(docs);
                        const signed = all.filter((d) => {
                            const t = String(d.documentType || d.document_type || d.type || '').toLowerCase();
                            return t === 'signed_invoice' || t === 'signed-invoice' || t === 'signed';
                        });
                        return { status: 'OK', data: signed.length ? signed : all, _source: 'documents-fallback' };
                    }
                } catch (fallbackErr) {
                    console.log(fallbackErr.response);
                }
            }
            console.log(err.response);
            return {
                status: 'ERROR',
                data: [],
                message: (err.response && err.response.data && err.response.data.message)
                    || 'Failed to load signed invoices. Backend signed-invoice API is required.',
            };
        }
    }

    /**
     * Persist the client-generated (unsigned) PDF so every invoice keeps a
     * durable reference alongside create data + signed uploads.
     */
    async uploadGeneratedPdf(invoiceId, file, meta = {}) {
        if (!invoiceId) return { status: 'ERROR', message: 'Invoice id is required.' };
        if (!file) return { status: 'ERROR', message: 'Generated PDF file is required.' };
        const formData = new FormData();
        formData.append('file', file);
        formData.append('generated_pdf', file);
        formData.append('document', file);
        formData.append('documentType', 'generated_pdf');
        if (meta.source) formData.append('source', meta.source);

        try {
            const res = await axios.post(
                API_URL + 'invoice/' + encodeURIComponent(invoiceId) + '/generated-pdf',
                formData,
                { headers: AuthService.authHeaderForm() }
            );
            return res.data;
        } catch (err) {
            const code = err && err.response && err.response.status;
            if (code === 404 || code === 501 || code === 405) {
                try {
                    const fallback = await this.uploadDocument(invoiceId, formData);
                    if (this._isOkEnvelope(fallback)) {
                        return { ...fallback, _source: 'documents-fallback' };
                    }
                    return fallback || { status: 'ERROR', message: 'Failed to store generated PDF.' };
                } catch (fallbackErr) {
                    console.log(fallbackErr.response);
                    return fallbackErr.response || { status: 'ERROR', message: 'Failed to store generated PDF.' };
                }
            }
            console.log(err.response);
            return (err.response && err.response.data) || err.response || {
                status: 'ERROR',
                message: 'Failed to store generated PDF. Backend generated-pdf API is required.',
            };
        }
    }

    /**
     * Download invoice PDF bytes from the server (generated or primary file).
     * Returns { status, blob, filename } on success.
     */
    async downloadInvoicePdf(invoiceId) {
        if (!invoiceId) return { status: 'ERROR', message: 'Invoice id is required.' };
        try {
            const res = await axios.get(
                API_URL + 'invoice/' + encodeURIComponent(invoiceId) + '/pdf',
                {
                    headers: AuthService.authHeader(),
                    responseType: 'blob',
                }
            );
            const disposition = res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition']);
            let filename = `invoice_${invoiceId}.pdf`;
            if (disposition) {
                const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
                if (match && match[1]) filename = decodeURIComponent(match[1].trim());
            }
            // Some APIs wrap errors as JSON blobs with application/json content-type
            const ctype = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || '';
            if (ctype.includes('application/json')) {
                const text = await res.data.text();
                let parsed = null;
                try { parsed = JSON.parse(text); } catch (_) { /* ignore */ }
                return {
                    status: 'ERROR',
                    message: (parsed && parsed.message) || 'PDF download failed.',
                };
            }
            return { status: 'OK', blob: res.data, filename };
        } catch (err) {
            console.log(err.response);
            const data = err.response && err.response.data;
            let message = 'Invoice PDF not available from server yet.';
            if (data && typeof data === 'object' && !(data instanceof Blob) && data.message) {
                message = data.message;
            }
            return { status: 'ERROR', message, httpStatus: err.response && err.response.status };
        }
    }

    // Candidate Revenue Ledger
    async getCandidateRevenueLedger(filters) {
        try {
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate)   params.endDate   = filters.endDate;
            if (filters.client)    params.client    = filters.client;
            const res = await axios.get(API_URL + 'invoice/candidate-revenue-ledger', {
                params,
                headers: AuthService.authHeader()
            });
            if (res?.data?.status === 'OK' && Array.isArray(res.data.data)) return res.data;
            if (Array.isArray(res?.data)) return { status: 'OK', data: res.data };
            return res.data;
        } catch (err) {
            console.log('[EnhancedInvoiceService] getCandidateRevenueLedger error:', err?.response?.status || err?.message);
            return { status: 'ERROR', data: [] };
        }
    }

    // GSTR-1
    async getGSTR1Data(filters) {
        try {
            const params = {};
            if (filters.period) params.period = filters.period;
            const res = await axios.get(API_URL + 'invoice/gstr1', {
                params,
                headers: AuthService.authHeader()
            });
            if (res?.data?.status === 'OK' && Array.isArray(res.data.data)) return res.data;
            if (Array.isArray(res?.data)) return { status: 'OK', data: res.data };
            return res.data;
        } catch (err) {
            console.log('[EnhancedInvoiceService] getGSTR1Data error:', err?.response?.status || err?.message);
            return { status: 'ERROR', data: [] };
        }
    }

    // GSTR-3B
    async getGSTR3BData(filters) {
        try {
            const params = {};
            if (filters.period) params.period = filters.period;
            const res = await axios.get(API_URL + 'invoice/gstr3b', {
                params,
                headers: AuthService.authHeader()
            });
            if (res?.data?.status === 'OK' && (Array.isArray(res.data.data) || res.data.summary)) return res.data;
            if (Array.isArray(res?.data)) {
                const summary = res.data.reduce((acc, it) => ({
                    taxable_value: (acc.taxable_value || 0) + (it.taxable_value || 0),
                    cgst:          (acc.cgst          || 0) + (it.cgst          || 0),
                    sgst:          (acc.sgst          || 0) + (it.sgst          || 0),
                    igst:          (acc.igst          || 0) + (it.igst          || 0),
                }), {});
                return { status: 'OK', data: res.data, summary };
            }
            return res.data;
        } catch (err) {
            console.log('[EnhancedInvoiceService] getGSTR3BData error:', err?.response?.status || err?.message);
            return { status: 'ERROR', data: [] };
        }
    }

    // Record Payment
    async recordPayment(paymentData) {
        try {
            const res = await axios.post(API_URL + 'invoice/payments', paymentData, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Add Follow-up
    async addFollowUp(followUpData) {
        try {
            const payload = {
                ...followUpData,
                next_follow_up: followUpData.next_follow_up
                    ?? followUpData.next_follow_up_date
                    ?? null,
            };
            delete payload.next_follow_up_date;
            const res = await axios.post(API_URL + 'invoice/follow-ups', payload, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response?.data || { status: 'ERROR', message: err.message || 'Failed to add follow-up' };
        }
    }

    async getFollowUps(invoiceId) {
        try {
            const res = await axios.get(API_URL + 'invoice/' + invoiceId + '/follow-ups', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response?.data || { status: 'ERROR', data: [] };
        }
    }

    // Upload Contractual Billing Sheet
    async uploadContractualBillingSheet(invoiceId, fileData) {
        try {
            const formData = new FormData();
            formData.append('file', fileData);
            const key = encodeURIComponent(String(invoiceId));
            const res = await axios.post(API_URL + 'invoice/' + key + '/attendance-sheet', formData, {
                headers: AuthService.authHeaderForm()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            const data = err.response && err.response.data;
            const httpStatus = err.response && err.response.status;
            const apiMessage = (data && (data.message || data.error)) || '';
            let message = 'Failed to upload billing sheet';
            if (httpStatus === 404 || /invoice not found/i.test(String(apiMessage))) {
                message = 'Invoice not found. Confirm the invoice number and try again.';
            } else if (apiMessage && !/^request failed with status code/i.test(String(apiMessage))) {
                message = String(apiMessage);
            }
            return { status: 'ERROR', message, httpStatus: httpStatus || null, data: data || null };
        }
    }

    // Add Line Items to Invoice
    async addLineItems(invoiceId, lineItems) {
        try {
            const res = await axios.post(API_URL + 'invoice/' + invoiceId + '/items', {
                line_items: lineItems
            }, {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }

    // Finance Dashboard
    async getFinanceDashboardData(filters) {
        try {
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate)   params.endDate   = filters.endDate;
            if (filters.client)    params.client    = filters.client;
            const res = await axios.get(API_URL + 'invoice/finance-dashboard', {
                params,
                headers: AuthService.authHeader()
            });
            if (res?.data?.status === 'OK' && res.data.data) return res.data;
            if (res?.data?.monthly_revenue != null || res?.data?.total_invoices != null) return { status: 'OK', data: res.data };
            return res.data;
        } catch (err) {
            console.log('[EnhancedInvoiceService] getFinanceDashboardData error:', err?.response?.status || err?.message);
            return { status: 'ERROR', data: {} };
        }
    }

    // Recruiter Dashboard
    async getRecruiterDashboardData(filters) {
        try {
            const params = {};
            if (filters.startDate)   params.startDate   = filters.startDate;
            if (filters.endDate)     params.endDate     = filters.endDate;
            if (filters.recruiterId) params.recruiterId = filters.recruiterId;
            const res = await axios.get(API_URL + 'invoice/recruiter-dashboard', {
                params,
                headers: AuthService.authHeader()
            });
            if (res?.data?.status === 'OK' && Array.isArray(res.data.data)) return res.data;
            if (Array.isArray(res?.data)) return { status: 'OK', data: res.data };
            return res.data;
        } catch (err) {
            console.log('[EnhancedInvoiceService] getRecruiterDashboardData error:', err?.response?.status || err?.message);
            return { status: 'ERROR', data: [] };
        }
    }

    // Reconciliation & Compliance
    async getReconciliationData(filters) {
        try {
            const params = {
                sd:            filters.startDate    || '',
                ed:            filters.endDate      || '',
                status:        filters.status       || '',
                client:        filters.client       || '',
                category:      filters.category     || '',
                bookingStatus: filters.bookingStatus || '',
                dateType:      'invoice_date',
            };
            const res = await axios.get(API_URL + 'invoice/reconciliation', {
                params,
                headers: AuthService.authHeader()
            });
            if (res?.data?.status === 'OK' && res.data.data) return res.data;
            return res.data;
        } catch (err) {
            console.log('[EnhancedInvoiceService] getReconciliationData error:', err?.response?.status || err?.message);
            return { status: 'ERROR', data: { invoices: [], statistics: {}, auditTrail: [] } };
        }
    }

    // Client Payment Terms
    async getClientPaymentTerms(clientId) {
        try {
            const res = await axios.get(API_URL + 'invoice/client/' + clientId + '/payment-terms', {
                headers: AuthService.authHeader()
            });
            return res.data;
        } catch (err) {
            console.log(err.response);
            return err.response;
        }
    }
}

export default new EnhancedInvoiceService();
